import { useRef } from 'react';

import { Flag32 as Flag } from '@weston/react-world-flags';
import { ScaleThreshold, scaleLinear, scaleThreshold } from 'd3-scale';
import { Color, PickingInfo } from 'deck.gl';
import Draggable from 'react-draggable';

import { ThemeProvider, rgbToHex, useTheme } from '@mui/material/styles';

import { IconButton } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CloseIcon from '@mui/icons-material/Close';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import { getAvatarByManufacturerName } from '@/utils';

import dayjs from '@/dayjs';

import { useFormatters } from '@/hooks';

import { BookingWithDailyAverageCo2e } from '../ManageCo2e';

// Gray color for N/A
const naRgbColor: Color = [110, 110, 110];

interface MapTooltipProps {
  popup: PickingInfo;
  colorScale: ScaleThreshold<number, Color>;
  elevation: number;
  onClose: () => void;
}

function MapTooltip(props: MapTooltipProps) {
  const { popup, colorScale, elevation, onClose } = props;
  const theme = useTheme();

  if (!popup || !popup.object) {
    return null;
  }

  const { object } = popup;

  const { formatNumber } = useFormatters();

  const rgbColor = object.co2eq_kg_daily_average
    ? colorScale(object.co2eq_kg_daily_average)
    : naRgbColor;

  const hexColor = rgbToHex(
    `rgb(${rgbColor[0]}, ${rgbColor[1]}, ${rgbColor[2]})`,
  );

  const nodeRef = useRef<HTMLDivElement | null>(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-handle"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper
        ref={nodeRef}
        elevation={elevation}
        sx={{
          zIndex: 3,
          position: 'absolute',
          backgroundColor: 'white',
          left: popup.x,
          top: popup.y,
          m: 2,
          p: 2,
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            flexGrow={1}
          >
            <DragHandleIcon id="draggable-handle" sx={{ cursor: 'move' }} />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={'center'}
              flexGrow={1}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography noWrap>{object.start_long_location}</Typography>
                <Box sx={{ pt: 0.5 }}>
                  <Flag code={object.start_iso as string} height={14} />
                </Box>
              </Stack>
              <ArrowRightAltIcon />

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
              >
                <Typography noWrap>{object.end_long_location}</Typography>
                <Box sx={{ pt: 0.5 }}>
                  <Flag code={object.end_iso as string} height={14} />
                </Box>
              </Stack>
            </Stack>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              color="primary"
              label={`${object.bookings.length} trip${object.bookings.length > 1 ? 's' : ''}`}
              sx={{ borderRadius: 1 }}
            />
            <Chip
              variant="filled"
              size="small"
              label={
                object.co2eq_kg_daily_average
                  ? `Daily Average CO₂e: ${formatNumber(object.co2eq_kg_daily_average)} kg`
                  : `Daily Average CO₂e: N/A`
              }
              sx={{ bgcolor: hexColor, borderRadius: 1, flexGrow: 1 }}
            />
          </Stack>
          <Divider />
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ maxHeight: 300, overflowY: 'auto' }}
          >
            <Table size="small" aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Reefer ID</TableCell>
                  <TableCell align="right">Model</TableCell>
                  <TableCell align="right">ATD</TableCell>
                  <TableCell align="right">ATA</TableCell>
                  <TableCell align="right">CO₂e</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {object.bookings.map((booking: BookingWithDailyAverageCo2e) => (
                  <TableRow
                    key={booking.internal_booking_id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography variant="caption">
                        {booking.reefer_id}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={1}
                      >
                        <Avatar
                          variant="rounded"
                          slotProps={{
                            img: { sx: { objectFit: 'contain' } },
                          }}
                          src={getAvatarByManufacturerName(
                            booking.manufacturer as string,
                          )}
                          sx={{
                            p: 0.5,
                            width: 36,
                            height: 36,
                          }}
                        >
                          {booking.manufacturer?.charAt(0)}
                        </Avatar>
                        <Typography variant="caption">
                          {booking.refrigeration_unit_model || 'Unknown'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {dayjs(booking.start_datetime).format('ll')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {dayjs(booking.end_datetime).format('ll')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {booking.co2eq_kg
                          ? `${formatNumber(booking.co2eq_kg)} kg`
                          : `N/A`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </Draggable>
  );
}

export { MapTooltip };
